import { useState, useEffect } from "react";
import { IInvoiceGridProps } from "../../typings/models/Invoicegridprops";
import InvoiceGridRow from "./InvoiceGridRow";
import { downloadInvoiceHistory } from "../../api/apimethods";
import "../../../src/styles//LabStyles.css";
import Loader from "../../components/molecules/Loader";
import { NotifyType } from "../../utils/NotifyType";
import { notify } from "../../utils";
const InvoiceGrid: React.FC<IInvoiceGridProps> = ({
  data,
  isLoading,
  headerColumns,
  fromDate,
  toDate,
}) => {
  const { message } = notify();
  const [isDownloading, setDownloading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>("invoiceDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const itemsPerPage = 10;
  const maxPagesToShow = 5;
  const sortData = (
    data: any[],
    column: string | null,
    direction: "asc" | "desc"
  ) => {
    if (!column) return data;

    return [...data].sort((a, b) => {
      let aValue = a[column];
      let bValue = b[column];
      if (column === "invoiceDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);

        if (isNaN(aValue.getTime())) aValue = new Date(0);
        if (isNaN(bValue.getTime())) bValue = new Date(0);
      }

      if (aValue < bValue) {
        return direction === "asc" ? -1 : 1;
      } else if (aValue > bValue) {
        return direction === "asc" ? 1 : -1;
      } else {
        return 0;
      }
    });
  };
  const [sortedData, setSortedData] = useState<any[]>(
    sortData(data, "invoiceNumber", "desc")
  );

  useEffect(() => {
    sortData(data, sortColumn, sortDirection);
  }, [data, sortColumn, sortDirection]);

  const handlePagination = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  const filteredData = data.filter((item) =>
    Object.values(item).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleDownload = async () => {
    try {
        setDownloading(true);

        if (!fromDate) {
          message(NotifyType.Error, "Please enter From date");
          setDownloading(false);
          return;
        }      
  
        if (!toDate) {
          message(NotifyType.Error, "Please enter To date");
          setDownloading(false);
          return;
        }      
  
        if ((fromDate && !toDate) || (!fromDate && toDate)) {
          message(NotifyType.Error, "Please enter both From and To dates");
          setDownloading(false);
          return;
        }
        
        const response = await downloadInvoiceHistory(fromDate || null, toDate || null);
        const filedata = response?.data?.zipBase64;
        const getUserInfo = localStorage.getItem("app-data");
        const userInfo = JSON.parse(getUserInfo || "{}");
        const filename = `${userInfo?.user?.idNo || "User"}_Invoices.zip`;
        
        // Create a link to download the file
        if (filedata) {
          setDownloading(false);
          const byteCharacters = atob(filedata);
          const byteArray = new Uint8Array(
            Array.from(byteCharacters, c => c.charCodeAt(0))
          );
          const blob = new Blob([byteArray], { type: "application/zip" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
        }
      } catch (error) {
          setDownloading(false);
        console.error('Download failed:', error);
    }
};

  const totalRecords = filteredData.length;
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const dataToShow = filteredData.slice(startIndex, endIndex);
  const renderPageButtons = () => {
    const pageButtons = [];
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = startPage + maxPagesToShow - 1;

      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = endPage - maxPagesToShow + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePagination(i)}>
            {i}
          </button>
        </li>
      );
    }

    if (totalPages > maxPagesToShow && endPage < totalPages) {
      pageButtons.push(
        <li key="separator" className="page-item disabled">
          <button className="page-link">...</button>
        </li>
      );
      pageButtons.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePagination(totalPages)}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pageButtons;
  };

  return (
    <div className="container">
      <div className="info-content">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="col-12 col-md-3">
            <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearch}
                className="form-control me-3 search-input csp-maxw-300"
            />
          </div>
            {isDownloading  && <Loader />}
          <div className="col-12 col-md-8 csp-text-right csp-pr-10 csp-pt-5">
            <span className="csp-italic-small csp-text-right">
              The maximum number of files that can be downloaded is 500
            </span>
          </div>
          <div className="col-12 col-md-1">
            <button
                type="button"
                className="amendment"
                onClick={handleDownload}>
                Download All
            </button>
          </div>
        </div>

        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              {headerColumns.map((column, index) => (
                <th
                  key={index}
                  className="grid-header csp-text-left"
                  onClick={() => handleSort(column.sortKey)}
                >
                  {column.columnName}                 
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataToShow.map((item, ind) => (
              <InvoiceGridRow
                key={ind}
                data={{ ...item, sno: startIndex + ind + 1 }}
              />
            ))}
          </tbody>
        </table>
        {isLoading && <Loader />}

        <nav aria-label="Page navigation example">
          <ul className="pagination">
            <li><button className="page-link">Total Records:{totalRecords}</button></li>
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePagination(currentPage - 1)}
              >
                Previous
              </button>
            </li>
            {renderPageButtons()}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePagination(currentPage + 1)}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
      <br />
    </div>
  );
};

export default InvoiceGrid;
